import { Typography, useMediaQuery } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import { ReactNode } from "react";

import theme from "../theme";

export type APHeaderRow = {
  title?: string;
  titleTooltip?: string;
};

export interface APTableProps<T extends Record<string, string>> {
  columns: Array<keyof T>;
  headerRows: Partial<Record<keyof T, APHeaderRow>>;
  rows: Array<Partial<Record<keyof T, string>>>;
  rowRenderer?: (
    row: Partial<Record<keyof T, string>>
  ) => Partial<Record<keyof T, ReactNode>>;
  totalRow?: Partial<Record<keyof T, string>>;
  showTotalRow?: boolean;
  highlightRows?: boolean;
  hideColumnsOnMobile?: Array<keyof T>;
}

const APTable = <T extends Record<string, string>>({
  columns,
  headerRows,
  rows,
  rowRenderer,
  totalRow,
  showTotalRow = false,
  highlightRows = false,
  hideColumnsOnMobile = [],
}: APTableProps<T>) => {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const _columns = isMobile
    ? columns.filter(
        (column) =>
          !hideColumnsOnMobile || !hideColumnsOnMobile.includes(column)
      )
    : columns;

  return (
    <TableContainer>
      <Table sx={{ minWidth: 340, boxShadow: "none" }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: "primary.light" }}>
            {_columns.map((column) => {
              const row = headerRows[column];

              return (
                <Tooltip key={column as string} title={row?.titleTooltip}>
                  <TableCell sx={{ p: 1.5 }}>
                    <Typography variant="titleSmall">
                      {row?.title || ""}
                    </Typography>
                  </TableCell>
                </Tooltip>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row, index) => {
            const renderedRow = rowRenderer ? rowRenderer(row) : row;

            return (
              <TableRow
                key={index}
                sx={{
                  "&:nth-of-type(odd)": {
                    backgroundColor: highlightRows
                      ? "secondary.light"
                      : "neutralGray.light",
                  },
                }}
              >
                {_columns.map((column) => {
                  const renderedValue = renderedRow[column];
                  const value = row[column];

                  return (
                    <TableCell key={column as string} sx={{ p: 1.5 }}>
                      {renderedValue ? (
                        renderedValue
                      ) : (
                        <Typography variant="bodyMedium">{value}</Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}

          {showTotalRow && totalRow && (
            <TableRow
              sx={{
                borderTop: "2px solid",
                borderBottom: "2px solid",
                borderColor: "black",
              }}
            >
              {_columns.map((column) => (
                <TableCell
                  key={column as string}
                  sx={{ p: 1.5, borderWidth: 2 }}
                >
                  <Typography variant="titleSmall">
                    {totalRow[column] || ""}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default APTable;
